import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  TextField,
  Button,
  Grid,
  Typography,
  Alert,
  Card,
  CardContent,
  IconButton,
  InputAdornment,
  Divider,
  alpha,
  useTheme,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Stack,
  Container,
  Chip,
  Tooltip,
  Fade,
  Snackbar,
  Alert as MuiAlert,
} from '@mui/material';
import {
  Save as SaveIcon,
  Clear as ClearIcon,
  Person as PersonIcon,
  DateRange as DateIcon,
  AttachMoney as MoneyIcon,
  Phone as PhoneIcon,
  Assignment as AssignmentIcon,
  LocationOn as LocationIcon,
  Badge as BadgeIcon,
  Work as WorkIcon,
  Description as DescriptionIcon,
  CheckCircle as CheckCircleIcon,
  Receipt as ReceiptIcon,
  Info as InfoIcon,
  MiscellaneousServices as ServiceIcon,
} from '@mui/icons-material';
import { useForm, Controller, FormProvider } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useAuth } from '../contexts/AuthContext';
import { passportService } from '../services/passportService';
import { ServiceName } from '../types/index';

// ─── Types ───────────────────────────────────────────────────────────────────

interface FormData {
  applicants_name: string;
  applicants_profession: string;
  applicants_type: string;
  passport_no: string;
  passport_type: string;
  date_of_entry: string;
  iqama_number?: string;
  mobile_number: string;
  expiry_date_of_iqama?: string;
  employer_name?: string;
  place_area: string;
  enrolment_no: string;
  money_receipt_no?: string;
  amount_jd?: number;
  remarks?: string;
  /** Stores the integer service_code chosen from the dropdown */
  service_code: number | '';
}

// ─── Validation schema ────────────────────────────────────────────────────────

const validationSchema = yup.object({
  applicants_type: yup.string().required('Applicant type is required'),
  applicants_name: yup
    .string()
    .required('Name is required')
    .min(2, 'Name must be at least 2 characters')
    .max(1024, 'Name is too long'),
  passport_type: yup.string().required('Passport type is required'),
  passport_no: yup
    .string()
    .required('Passport number is required')
    .min(6, 'Passport number must be at least 6 characters')
    .max(50, 'Passport number is too long'),
  date_of_entry: yup
    .string()
    .required('Date of entry is required')
    .test('is-today', 'Date of entry must be today', function (value) {
      if (!value) return false;
      const today = new Date().toISOString().split('T')[0];
      return value === today;
    }),
  mobile_number: yup
    .string()
    .matches(
      /^07\d{8}$/,
      'Please enter a valid Jordanian mobile number (e.g., 07XXXXXXXX)',
    )
    .required('Mobile number is required'),
  place_area: yup
    .string()
    .required('Place/Area is required')
    .max(1024, 'Place/Area is too long'),
  enrolment_no: yup
    .string()
    .required('Enrolment number is required')
    .max(50, 'Enrolment number is too long')
    .test(
      'enrolment-format',
      'Enrolment number must be at least 5 characters, or follow the MRP auto-generated format (MRPDDMMYYYYHHmmss)',
      function (value) {
        if (!value) return false;
        // MRP pattern takes priority – always valid if it matches
        if (/^MRP\d{14}$/.test(value)) return true;
        // Otherwise apply normal minimum-length rule
        return value.trim().length >= 5;
      },
    ),
  iqama_number: yup.string().max(50, 'Iqama number is too long'),
  expiry_date_of_iqama: yup
    .string()
    .nullable()
    .test(
      'iqama-expiry-required',
      'Expiry date is required when iqama number is provided',
      function (value) {
        const iqamaNumber = this.parent.iqama_number;
        if (iqamaNumber && iqamaNumber.trim() !== '' && (!value || value.trim() === '')) {
          return false;
        }
        return true;
      },
    ),
  employer_name: yup.string().max(1024, 'Employer name is too long'),
  money_receipt_no: yup.string().max(50, 'Receipt number is too long'),
  amount_jd: yup
    .number()
    .positive('Amount must be positive')
    .max(9999999.99, 'Amount is too large')
    .nullable()
    .transform((value) => (isNaN(value) ? null : value)),
  remarks: yup.string().max(5000, 'Remarks are too long'),
  applicants_profession: yup.string().max(5000, 'Applicants Profession is too long'),
  service_code: yup
    .number()
    .required('Service is required')
    .typeError('Service is required'),
});

// ─── Component ────────────────────────────────────────────────────────────────

const DataEntryForm: React.FC = () => {
  const theme = useTheme();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [completionPercentage, setCompletionPercentage] = useState(0);
  const [successAlertOpen, setSuccessAlertOpen] = useState(false);

  // Service-name dropdown state
  const [serviceNames, setServiceNames] = useState<ServiceName[]>([]);
  const [serviceNamesLoading, setServiceNamesLoading] = useState(false);
  const [serviceNamesError, setServiceNamesError] = useState<string | null>(null);

  // ── Helpers ──────────────────────────────────────────────────────────────

  const getTodayDate = () => new Date().toISOString().split('T')[0];

  const formatDate = (dateString: string | undefined): string => {
    if (!dateString) return 'Not entered';
    try {
      const [day, month, year] = dateString.split('-');
      const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
      return `${year}-${months[parseInt(month) - 1]}-${parseInt(day)}`;
    } catch {
      return 'Invalid date';
    }
  };

  // ── Form setup ───────────────────────────────────────────────────────────

  const emptyDefaults: FormData = {
    applicants_name: '',
    applicants_profession: '',
    applicants_type: '',
    passport_no: '',
    passport_type: '',
    date_of_entry: getTodayDate(),
    iqama_number: '',
    mobile_number: '',
    expiry_date_of_iqama: '',
    employer_name: '',
    place_area: '',
    enrolment_no: '',
    money_receipt_no: '',
    amount_jd: undefined,
    remarks: '',
    service_code: '',
  };

  const methods = useForm<FormData>({
    resolver: yupResolver(validationSchema as any),
    defaultValues: emptyDefaults,
    mode: 'onChange',
  });

  const {
    handleSubmit,
    control,
    reset,
    formState: { errors },
    watch,
    setValue,
  } = methods;

  const watchApplicantType = watch('applicants_type');
  const watchPassportType  = watch('passport_type');
  const watchIqamaNumber = watch('iqama_number');
  const watchAllFields = watch();
  const watchServiceCode = watch('service_code');

  // ── Load service names on mount ──────────────────────────────────────────

  useEffect(() => {
    let cancelled = false;

    const fetchServiceNames = async () => {
      setServiceNamesLoading(true);
      setServiceNamesError(null);
      try {
        const data = await passportService.getServiceNames();
        if (!cancelled) setServiceNames(data);
      } catch {
        if (!cancelled) setServiceNamesError('Failed to load services. Please refresh.');
      } finally {
        if (!cancelled) setServiceNamesLoading(false);
      }
    };

    fetchServiceNames();
    return () => { cancelled = true; };
  }, []);

  // ── Form completion percentage ───────────────────────────────────────────

  useEffect(() => {
    const requiredFields = [
      'applicants_type',
      'applicants_name',
      'passport_type',
      'passport_no',
      'mobile_number',
      'place_area',
      'enrolment_no',
      'money_receipt_no',
      'amount_jd',
      'service_code',    // included in completion tracking
    ];

    const completedFields = requiredFields.filter((field) => {
      const value = watchAllFields[field as keyof FormData];
      return value !== undefined && value !== null && value.toString().trim().length > 0;
    }).length;

    setCompletionPercentage(Math.round((completedFields / requiredFields.length) * 100));
  }, [watchAllFields]);

  // ── Auto-clear expiry date when iqama is cleared ─────────────────────────

  useEffect(() => {
    if (!watchIqamaNumber || watchIqamaNumber.trim() === '') {
      setValue('expiry_date_of_iqama', '');
    }
  }, [watchIqamaNumber, setValue]);

  // ── Auto-set employer for INDIVIDUAL ─────────────────────────────────────

  useEffect(() => {
    if (watchApplicantType === 'INDIVIDUAL') {
      setValue('employer_name', 'Self');
    } else if (watch('employer_name') === 'Self') {
      setValue('employer_name', '');
    }
  }, [watchApplicantType, setValue]);

  // ── MRP: auto-generate enrolment number, lock field ──────────────────────

  /**
   * Generate the MRP enrolment code from the current moment:
   *   MRP  +  DD  MM  YYYY  HH  mm  ss   →  17 characters
   * Example: MRP17022026153045
   */
  const generateMrpEnrolmentNo = (): string => {
    const now = new Date();
    const pad = (n: number, len = 2) => String(n).padStart(len, '0');
    return (
      'MRP' +
      pad(now.getDate()) +
      pad(now.getMonth() + 1) +
      now.getFullYear() +
      pad(now.getHours()) +
      pad(now.getMinutes()) +
      pad(now.getSeconds())
    );
  };

  useEffect(() => {
    if (watchPassportType === 'MRP') {
      // Generate once when MRP is selected; clear if user switches away
      setValue('enrolment_no', generateMrpEnrolmentNo(), { shouldValidate: true });
    } else if (watchPassportType && watchPassportType !== 'MRP') {
      // Only clear if it previously held an MRP-generated value
      const current = watch('enrolment_no');
      if (current && current.startsWith('MRP')) {
        setValue('enrolment_no', '', { shouldValidate: false });
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchPassportType, setValue]);

  const expiryDateValue = watch('expiry_date_of_iqama');
  const isPastIqamaDate = expiryDateValue
    ? new Date(expiryDateValue) < new Date(new Date().setHours(0, 0, 0, 0))
    : false;

  // ── Derive selected service label for the summary panel ──────────────────

  const selectedServiceLabel = React.useMemo(() => {
    if (!watchServiceCode) return 'Not selected';
    const found = serviceNames.find((s) => s.service_code === Number(watchServiceCode));
    return found ? `${found.service_name} (${found.service_code})` : String(watchServiceCode);
  }, [watchServiceCode, serviceNames]);

  // ── Submit ────────────────────────────────────────────────────────────────

  const onSubmit = async (data: FormData) => {
    try {
      setLoading(true);
      setError(null);
      setSuccessMessage(null);

      // Clean up empty strings
      const cleanedData = Object.fromEntries(
        Object.entries(data).map(([key, value]) => [
          key,
          value === '' || value === undefined ? null : value,
        ]),
      ) as FormData;

      const formattedData = {
        ...cleanedData,
        date_of_entry: new Date(cleanedData.date_of_entry).toISOString().split('T')[0],
        expiry_date_of_iqama: cleanedData.expiry_date_of_iqama
          ? new Date(cleanedData.expiry_date_of_iqama).toISOString().split('T')[0]
          : null,
        amount_jd: cleanedData.amount_jd || null,
        service_code: cleanedData.service_code ? Number(cleanedData.service_code) : null,
        created_by: user?.users_name || 'System',
        status: 'PENDING',
        data_entry_officer_id: user?.users_id,
      };

      const response = await passportService.createRecord(formattedData as any);

      const successMsg = `✅ Record created successfully!\nID: ${response.passport_applicants_id}\nEnrolment No: ${response.enrolment_no}\nApplicant: ${response.applicants_name}`;
      setSuccessMessage(successMsg);
      setSuccessAlertOpen(true);

      setTimeout(() => {
        reset(emptyDefaults);
        setSuccessAlertOpen(false);
        setSuccessMessage(null);
      }, 5000);
    } catch (err: any) {
      // api.ts handleError() rejects with: { status, message, data: <DRF body> }
      // DRF field errors live in err.data, e.g. { enrolment_no: ["..."] }
      // Fallback chain covers raw axios errors and any future service refactors.
      const responseData: Record<string, any> =
        err?.data ??              // ← api.ts handleError shape  { status, message, data }
        err?.response?.data ??   // ← raw axios error shape
        {};

      const extract = (v: unknown): string =>
        Array.isArray(v) ? String(v[0]) : String(v ?? '');

      const fieldErrors: string[] = [];

      if (responseData.enrolment_no) {
        const msg = extract(responseData.enrolment_no);
        methods.setError('enrolment_no', { type: 'server', message: msg });
        fieldErrors.push(msg);
      }
      if (responseData.non_field_errors) {
        fieldErrors.push(extract(responseData.non_field_errors));
      }
      if (responseData.expiry_date_of_iqama) {
        const msg = extract(responseData.expiry_date_of_iqama);
        methods.setError('expiry_date_of_iqama', { type: 'server', message: msg });
        fieldErrors.push(msg);
      }
      if (responseData.date_of_entry) {
        const msg = extract(responseData.date_of_entry);
        methods.setError('date_of_entry', { type: 'server', message: msg });
        fieldErrors.push(msg);
      }
      if (responseData.passport_no) {
        const msg = extract(responseData.passport_no);
        methods.setError('passport_no', { type: 'server', message: msg });
        fieldErrors.push(msg);
      }

      setError(
        fieldErrors.length > 0
          ? fieldErrors.join(' ')
          : responseData.detail ||
            responseData.message ||
            err?.message ||
            'Failed to create record. Please try again.',
      );
    } finally {
      setLoading(false);
    }
  };

  // ── Reset ─────────────────────────────────────────────────────────────────

  const handleReset = () => {
    reset(emptyDefaults);
    setError(null);
    setSuccessMessage(null);
    setSuccessAlertOpen(false);
    setCompletionPercentage(0);
  };

  const handleCloseSuccessAlert = () => {
    setSuccessAlertOpen(false);
    setSuccessMessage(null);
  };

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <Container maxWidth="lg">
      {/* Success Snackbar */}
      <Snackbar
        open={successAlertOpen}
        autoHideDuration={5000}
        onClose={handleCloseSuccessAlert}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <MuiAlert
          onClose={handleCloseSuccessAlert}
          severity="success"
          variant="filled"
          sx={{ width: '100%', '& .MuiAlert-message': { whiteSpace: 'pre-line' } }}
        >
          {successMessage}
        </MuiAlert>
      </Snackbar>

      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)}>

          {/* ── Header ─────────────────────────────────────────────────── */}
          <Card sx={{ mb: 2, borderRadius: 3, boxShadow: 3 }}>
            <CardContent sx={{ p: 3 }}>
              <Grid container alignItems="center" justifyContent="space-between">
                <Grid size={{ xs: 12, md: 8 }}>
                  <Typography variant="h4" sx={{ fontWeight: 700, mb: 1, color: 'primary.main' }}>
                    New Application Form
                  </Typography>
                  <Typography variant="body1" color="text.secondary">
                    Please enter all required applicant details
                  </Typography>
                </Grid>
                <Grid size={{ xs: 12, md: 4 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 2 }}>
                    <Box sx={{ textAlign: 'right' }}>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                        Application Form Completion
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ width: 120, height: 8, bgcolor: 'grey.200', borderRadius: 4, overflow: 'hidden' }}>
                          <Box
                            sx={{
                              width: `${completionPercentage}%`,
                              height: '100%',
                              bgcolor: completionPercentage === 100 ? 'success.main' : 'primary.main',
                              transition: 'width 0.3s ease',
                            }}
                          />
                        </Box>
                        <Typography variant="body2" sx={{ fontWeight: 600, minWidth: 40 }}>
                          {completionPercentage}%
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* ── Error alert ────────────────────────────────────────────── */}
          {error && (
            <Fade in={!!error}>
              <Box sx={{ mb: 3 }}>
                <Alert severity="error" onClose={() => setError(null)} sx={{ borderRadius: 2, boxShadow: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>{error}</Typography>
                </Alert>
              </Box>
            </Fade>
          )}

          {/* ── Main grid ──────────────────────────────────────────────── */}
          <Grid container spacing={3}>

            {/* ══ LEFT COLUMN ══════════════════════════════════════════════ */}
            <Grid size={{ xs: 12, lg: 8 }}>
              <Card sx={{ borderRadius: 3, boxShadow: 2, height: '100%' }}>
                <CardContent sx={{ p: 4 }}>

                  {/* Personal Information heading */}
                  <Typography variant="h5" sx={{ fontWeight: 600, mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <PersonIcon color="primary" />
                    Personal Information
                  </Typography>

                  <Grid container spacing={3}>

                    {/* ── Service Dropdown ───────────────────────────────── */}
                    <Grid size={{ xs: 12, md: 6 }}>
                      <Controller
                        name="service_code"
                        control={control}
                        render={({ field }) => (
                          <FormControl
                            fullWidth
                            required
                            error={!!errors.service_code}
                            disabled={serviceNamesLoading}
                          >
                            <InputLabel>Service Type</InputLabel>
                            <Select
                              {...field}
                              label="Service *"
                              value={field.value ?? ''}
                              onChange={(e) => field.onChange(e.target.value)}
                              startAdornment={
                                <InputAdornment position="start">
                                  {serviceNamesLoading
                                    ? <CircularProgress size={18} />
                                    : <ServiceIcon color={errors.service_code ? 'error' : 'action'} />
                                  }
                                </InputAdornment>
                              }
                            >
                              {/* Placeholder option */}
                              <MenuItem value="" disabled>
                                <em>Select a service…</em>
                              </MenuItem>

                              {serviceNames.map((sn) => (
                                <MenuItem key={sn.service_name_id} value={sn.service_code}>
                                  {sn.service_name}
                                  <Typography
                                    component="span"
                                    variant="caption"
                                    sx={{ ml: 1, color: 'text.secondary' }}
                                  >
                                    ({sn.service_code})
                                  </Typography>
                                </MenuItem>
                              ))}
                            </Select>

                            <FormHelperText>
                              {errors.service_code?.message ||
                                serviceNamesError ||
                                'Select the service type for this application'}
                            </FormHelperText>
                          </FormControl>
                        )}
                      />
                    </Grid>

                    {/* ── Applicant Type ─────────────────────────────────── */}
                    <Grid size={{ xs: 12, md: 6 }}>
                      <Controller
                        name="applicants_type"
                        control={control}
                        render={({ field }) => (
                          <FormControl fullWidth error={!!errors.applicants_type}>
                            <InputLabel>Applicant Type *</InputLabel>
                            <Select
                              {...field}
                              label="Applicant Type"
                              required
                              startAdornment={
                                <InputAdornment position="start">
                                  <PersonIcon color="action" />
                                </InputAdornment>
                              }
                            >
                              <MenuItem value="INDIVIDUAL">Individual</MenuItem>
                              <MenuItem value="ORGANIZATION">Organization</MenuItem>
                            </Select>
                            <FormHelperText>{errors.applicants_type?.message}</FormHelperText>
                          </FormControl>
                        )}
                      />
                    </Grid>

                    {/* ── Employer Name ──────────────────────────────────── */}
                    <Grid size={{ xs: 12, md: 6 }}>
                      <Controller
                        name="employer_name"
                        control={control}
                        rules={{
                          required: watchApplicantType === 'ORGANIZATION'
                            ? 'Employer name is required for organizations'
                            : false,
                        }}
                        render={({ field, fieldState: { error } }) => (
                          <TextField
                            {...field}
                            fullWidth
                            label="Employer's Name"
                            disabled={watchApplicantType === 'INDIVIDUAL'}
                            required={watchApplicantType === 'ORGANIZATION'}
                            error={!!error}
                            helperText={
                              error?.message ||
                              (watchApplicantType === 'INDIVIDUAL'
                                ? 'Automatically set to "Self" for individual applicants'
                                : 'Required for organization applicants')
                            }
                            placeholder={watchApplicantType === 'INDIVIDUAL' ? 'Self' : 'Enter Employer Name'}
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <WorkIcon color={watchApplicantType === 'INDIVIDUAL' ? 'disabled' : 'action'} />
                                </InputAdornment>
                              ),
                            }}
                          />
                        )}
                      />
                    </Grid>

                    {/* ── Applicant Name ──────────────────────────────────────── */}
                    <Grid size={{ xs: 12, md: 6 }}>
                      <Controller
                        name="applicants_name"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            fullWidth
                            label="Applicant's Name"
                            placeholder="Enter Applicant's Full Name"
                            required
                            error={!!errors.applicants_name}
                            helperText={errors.applicants_name?.message}
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <PersonIcon color="action" />
                                </InputAdornment>
                              ),
                            }}
                          />
                        )}
                      />
                    </Grid>

                    {/* ── Mobile Number ──────────────────────────────────── */}
                    <Grid size={{ xs: 12, md: 6 }}>
                      <Controller
                        name="mobile_number"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            fullWidth
                            label="Mobile Number"
                            required
                            placeholder="Enter Mobile No: 079XXXXXXX"
                            error={!!errors.mobile_number}
                            helperText={errors.mobile_number?.message}
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <PhoneIcon color="action" />
                                </InputAdornment>
                              ),
                            }}
                          />
                        )}
                      />
                    </Grid>

                    {/* ── Passport Number ────────────────────────────────── */}
                    <Grid size={{ xs: 12, md: 6 }}>
                      <Controller
                        name="passport_no"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            fullWidth
                            label="Passport Number"
                            placeholder="Enter Passport Number"
                            required
                            error={!!errors.passport_no}
                            helperText={errors.passport_no?.message}
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <BadgeIcon color="action" />
                                </InputAdornment>
                              ),
                            }}
                          />
                        )}
                      />
                    </Grid>

                    {/* ── Passport Type ──────────────────────────────────── */}
                    <Grid size={{ xs: 12, md: 6 }}>
                      <Controller
                        name="passport_type"
                        control={control}
                        render={({ field }) => (
                          <FormControl fullWidth error={!!errors.passport_type}>
                            <InputLabel>Passport Type *</InputLabel>
                            <Select
                              {...field}
                              label="Passport Type"
                              required
                              startAdornment={
                                <InputAdornment position="start">
                                  <BadgeIcon color="action" />
                                </InputAdornment>
                              }
                            >
                              <MenuItem value="MRP">Machine Readable Passport (MRP)</MenuItem>
                              <MenuItem value="EPP">Electronic Passport (EPP)</MenuItem>
                            </Select>
                            <FormHelperText>{errors.passport_type?.message}</FormHelperText>
                          </FormControl>
                        )}
                      />
                    </Grid>

                    {/* ── Date of Entry ──────────────────────────────────── */}
                    <Grid size={{ xs: 12, md: 6 }}>
                      <Controller
                        name="date_of_entry"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            fullWidth
                            label="Date of Entry"
                            required
                            type="date"
                            InputLabelProps={{ shrink: true }}
                            error={!!errors.date_of_entry}
                            helperText={errors.date_of_entry?.message || "Only today's date is allowed"}
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <DateIcon color="action" />
                                </InputAdornment>
                              ),
                              readOnly: true,
                            }}
                            slotProps={{
                              input: {
                                inputProps: { min: getTodayDate(), max: getTodayDate() },
                              },
                            }}
                          />
                        )}
                      />
                    </Grid>

                    {/* ── Place/Area ─────────────────────────────────────── */}
                    <Grid size={{ xs: 12, md: 6 }}>
                      <Controller
                        name="place_area"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            fullWidth
                            label="Place/Area of Data Entry"
                            placeholder="Enter Place/Area"
                            required
                            error={!!errors.place_area}
                            helperText={errors.place_area?.message}
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <LocationIcon color="action" />
                                </InputAdornment>
                              ),
                            }}
                          />
                        )}
                      />
                    </Grid>
                    
                    {/* ── Applicant Profession ────────────────────────────────────────── */}
                    <Grid size={{ xs: 12, md: 6 }}>
                      <Controller
                        name="applicants_profession"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            fullWidth
                            label="Applicant's Profession Name"
                            placeholder="Enter Applicant's Profession Name"
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start" sx={{ alignSelf: 'flex-start', mt: 1 }}>
                                  <DescriptionIcon color="action" />
                                </InputAdornment>
                              ),
                            }}
                          />
                        )}
                      />
                    </Grid>

                    {/* ── Iqama Number ───────────────────────────────────── */}
                    <Grid size={{ xs: 12, md: 6 }}>
                      <Controller
                        name="iqama_number"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            fullWidth
                            label="Iqama Number"
                            placeholder="Enter iqama number"
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <BadgeIcon color="action" />
                                </InputAdornment>
                              ),
                            }}
                            helperText="If provided, expiry date is required"
                          />
                        )}
                      />
                    </Grid>

                    {/* ── Iqama Expiry Date ──────────────────────────────── */}
                    <Grid size={{ xs: 12, md: 6 }}>
                      <Controller
                        name="expiry_date_of_iqama"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            fullWidth
                            label="Iqama Expiry Date"
                            type="date"
                            InputLabelProps={{ shrink: true }}
                            error={!!errors.expiry_date_of_iqama}
                            helperText={
                              errors.expiry_date_of_iqama?.message ? (
                                errors.expiry_date_of_iqama.message
                              ) : isPastIqamaDate ? (
                                <span style={{ color: '#ed6c02', fontWeight: 700 }}>
                                  Please take full payment
                                </span>
                              ) : (
                                'Required when iqama number is provided'
                              )
                            }
                            disabled={!watchIqamaNumber || watchIqamaNumber.trim() === ''}
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <DateIcon color={watchIqamaNumber ? 'action' : 'disabled'} />
                                </InputAdornment>
                              ),
                            }}
                          />
                        )}
                      />
                    </Grid>

                    {/* ── Enrolment Number ───────────────────────────────── */}
                    <Grid size={{ xs: 12}}>
                      <Controller
                        name="enrolment_no"
                        control={control}
                        render={({ field }) => (
                          <Tooltip
                            title={
                              watchPassportType === 'MRP'
                                ? 'Auto-generated from the current date & time for MRP passports. This field cannot be edited.'
                                : ''
                            }
                            placement="top"
                            arrow
                          >
                            <TextField
                              {...field}
                              fullWidth
                              label="Enrolment Number"
                              placeholder={
                                watchPassportType === 'MRP'
                                  ? 'Auto-generated (MRPDDMMYYYYHHmmss)'
                                  : 'Enter Enrolment Number'
                              }
                              required
                              error={!!errors.enrolment_no}
                              helperText={
                                errors.enrolment_no?.message ||
                                (watchPassportType === 'MRP'
                                  ? '🔒 Auto-generated for MRP — not editable'
                                  : undefined)
                              }
                              InputProps={{
                                readOnly: watchPassportType === 'MRP',
                                startAdornment: (
                                  <InputAdornment position="start">
                                    <AssignmentIcon
                                      color={watchPassportType === 'MRP' ? 'disabled' : 'action'}
                                    />
                                  </InputAdornment>
                                ),
                              }}
                              sx={
                                watchPassportType === 'MRP'
                                  ? {
                                      '& .MuiInputBase-root': {
                                        bgcolor: (t) => t.palette.action.disabledBackground,
                                        cursor: 'not-allowed',
                                      },
                                    }
                                  : {}
                              }
                            />
                          </Tooltip>
                        )}
                      />
                    </Grid>                    

                    {/* ══ Payment Information section ═══════════════════════ */}
                    <Grid size={{ xs: 12 }}>
                      <Divider sx={{ my: 2 }} />
                      <Typography variant="h5" sx={{ fontWeight: 600, mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <ReceiptIcon color="primary" />
                        Payment Information
                      </Typography>
                    </Grid>

                    {/* ── Money Receipt Number ───────────────────────────── */}
                    <Grid size={{ xs: 12, md: 6 }}>
                      <Controller
                        name="money_receipt_no"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            fullWidth
                            label="Money Receipt Number"
                            placeholder="Enter Money Receipt Number"
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <ReceiptIcon color="action" />
                                </InputAdornment>
                              ),
                            }}
                          />
                        )}
                      />
                    </Grid>

                    {/* ── Amount (JD) ────────────────────────────────────── */}
                    <Grid size={{ xs: 12, md: 6 }}>
                      <Controller
                        name="amount_jd"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            fullWidth
                            label="Amount (JD)"
                            placeholder="Enter Amount (JD)"
                            type="number"
                            error={!!errors.amount_jd}
                            helperText={errors.amount_jd?.message}
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <MoneyIcon color="action" />
                                </InputAdornment>
                              ),
                              inputProps: { step: 0.01, min: 0 },
                            }}
                            onChange={(e) => {
                              field.onChange(e.target.value === '' ? '' : parseFloat(e.target.value));
                            }}
                            value={field.value || ''}
                            onWheel={(e) => (e.target as HTMLElement).blur()}
                            sx={{
                              '& input[type=number]': { MozAppearance: 'textfield' },
                              '& input[type=number]::-webkit-outer-spin-button': { WebkitAppearance: 'none', margin: 0 },
                              '& input[type=number]::-webkit-inner-spin-button': { WebkitAppearance: 'none', margin: 0 },
                            }}
                          />
                        )}
                      />
                    </Grid>

                    {/* ══ Additional Remarks section ════════════════════════ */}
                    <Grid size={{ xs: 12 }}>
                      <Divider sx={{ my: 2 }} />
                      <Typography variant="h5" sx={{ fontWeight: 600, mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <DescriptionIcon color="primary" />
                        Additional Remarks
                      </Typography>
                    </Grid>

                    {/* ── Remarks ────────────────────────────────────────── */}
                    <Grid size={{ xs: 12 }}>
                      <Controller
                        name="remarks"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            fullWidth
                            label="Remarks"
                            multiline
                            rows={6}
                            placeholder="Enter any additional notes or comments..."
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start" sx={{ alignSelf: 'flex-start', mt: 1 }}>
                                  <DescriptionIcon color="action" />
                                </InputAdornment>
                              ),
                            }}
                          />
                        )}
                      />
                    </Grid>

                  </Grid>{/* end inner grid */}
                </CardContent>
              </Card>
            </Grid>{/* end left column */}

            {/* ══ RIGHT COLUMN – Summary ════════════════════════════════════ */}
            <Grid size={{ xs: 12, lg: 4 }}>
              <Stack spacing={3}>
                <Card sx={{ borderRadius: 3, boxShadow: 2, bgcolor: alpha(theme.palette.primary.main, 0.03) }}>
                  <CardContent sx={{ p: 4 }}>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                      <InfoIcon color="primary" />
                      Quick Summary
                    </Typography>

                    <Stack spacing={1.5}>

                      {/* ── Service summary row ─────────────────────────── */}
                      <Box>
                        <Typography variant="caption" color="text.secondary">Service Type</Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 500,
                            color: watchServiceCode ? 'text.primary' : 'text.primary',
                          }}
                        >
                          {selectedServiceLabel}
                        </Typography>
                      </Box>

                      <Box>
                        <Typography variant="caption" color="text.secondary">Applicant Type</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {watch('applicants_type')
                            ? watch('applicants_type') === 'INDIVIDUAL' ? 'Individual' : 'Organization'
                            : 'Not selected'}
                        </Typography>
                      </Box>

                      <Box>
                        <Typography variant="caption" color="text.secondary">Employer's Name</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {watch('employer_name') || 'Not entered'}
                        </Typography>
                      </Box>

                      <Box>
                        <Typography variant="caption" color="text.secondary">Applicant's Name</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {watch('applicants_name') || 'Not entered'}
                        </Typography>
                      </Box>

                      <Box>
                        <Typography variant="caption" color="text.secondary">Mobile Number</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {watch('mobile_number') || 'Not entered'}
                        </Typography>
                      </Box>

                      <Box>
                        <Typography variant="caption" color="text.secondary">Passport Number</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {watch('passport_no') || 'Not entered'}
                        </Typography>
                      </Box>

                      <Box>
                        <Typography variant="caption" color="text.secondary">Passport Type</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {watch('passport_type') || 'Not entered'}
                        </Typography>
                      </Box>

                      <Box>
                        <Typography variant="caption" color="text.secondary">Date of Entry</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {formatDate(watch('date_of_entry'))}
                        </Typography>
                      </Box>

                      <Box>
                        <Typography variant="caption" color="text.secondary">Place/Area of Data Entry</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {watch('place_area') || 'Not entered'}
                        </Typography>
                      </Box>

                      <Box>
                        <Typography variant="caption" color="text.secondary">Applicant's Profession Name</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {watch('applicants_profession') || 'Not entered'}
                        </Typography>
                      </Box>

                      {watchIqamaNumber && (
                        <Box>
                          <Typography variant="caption" color="text.secondary">Iqama Number</Typography>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>{watchIqamaNumber}</Typography>
                        </Box>
                      )}

                      {watchIqamaNumber && (
                        <Box>
                          <Typography variant="caption" color="text.secondary">Iqama Expiry Date</Typography>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {formatDate(watch('expiry_date_of_iqama'))}
                          </Typography>
                        </Box>
                      )}

                      <Box>
                        <Typography variant="caption" color="text.secondary">Enrolment No</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {watch('enrolment_no') || 'Not entered'}
                        </Typography>
                      </Box>

                      <Box>
                        <Typography variant="caption" color="text.secondary">Money Receipt No.</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {watch('money_receipt_no') || 'Not entered'}
                        </Typography>
                      </Box>

                      <Box>
                        <Typography variant="caption" color="text.secondary">Amount (JD)</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {watch('amount_jd') || 'Not entered'}
                        </Typography>
                      </Box>

                      <Box>
                        <Typography variant="caption" color="text.secondary">Remarks</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {watch('remarks') || 'Not entered'}
                        </Typography>
                      </Box>

                    </Stack>
                  </CardContent>
                </Card>
              </Stack>
            </Grid>{/* end right column */}

          </Grid>{/* end main grid */}

          {/* ── Action buttons ─────────────────────────────────────────────── */}
          <Box sx={{ mt: 4, display: 'flex', justifyContent: 'space-between', gap: 2 }}>
            <Button
              variant="outlined"
              color="secondary"
              onClick={handleReset}
              startIcon={<ClearIcon />}
              disabled={loading}
              sx={{ px: 4, borderRadius: 2 }}
            >
              Clear Form
            </Button>

            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button
                type="submit"
                variant="contained"
                color="success"
                startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                disabled={loading || completionPercentage < 100}
                sx={{
                  px: 6,
                  borderRadius: 2,
                  fontWeight: 600,
                  boxShadow: 2,
                  '&:hover': { boxShadow: 4, transform: 'translateY(-1px)', transition: 'all 0.2s' },
                }}
              >
                {loading ? 'Saving…' : 'Save Record'}
              </Button>
            </Box>
          </Box>

          {/* Validation hint */}
          {completionPercentage < 100 && (
            <Alert
              severity="info"
              sx={{ mt: 3, borderRadius: 2, bgcolor: '#dce775' }}
              icon={<InfoIcon />}
            >
              <Typography variant="body2">
                Please complete all required fields (marked with *) before saving.
                Current completion: {completionPercentage}%
              </Typography>
            </Alert>
          )}

        </form>
      </FormProvider>
    </Container>
  );
};

export default DataEntryForm;